import { useState } from "react";
import { useLocation, useParams } from "wouter";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";

const RAZ_LOGO = "/manus-storage/raz_logo_negativo_200222c8.png";
const EyeIcon = () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>;
const EyeOffIcon = () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>;

export default function AceitarConviteCliente() {
  const params = useParams<{ token: string }>();
  const [, navigate] = useLocation();
  const { login: authLogin } = useAuth();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const acceptMutation = trpc.clientAuth.acceptInvite.useMutation({
    onSuccess: (data) => {
      authLogin({
        token:      data.token,
        userId:     data.userId,
        name:       data.name ?? "",
        email:      data.email,
        role:       (data.role ?? "viewer") as "admin" | "editor" | "viewer",
        clientId:   data.clientId ?? null,
        isRazAdmin: (data as any).isRazAdmin ?? false,
        mustChangePassword: false,
      });
      localStorage.removeItem("RAZ_DEV_CLIENT_ID");
      setSuccess(true);
      setTimeout(() => navigate("/"), 2000);
    },
    onError: (err) => { setError(err.message); },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("A senha deve ter pelo menos 8 caracteres."); return; }
    if (password !== confirmPassword) { setError("As senhas não coincidem."); return; }
    acceptMutation.mutate({ token: params.token, password });
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12 bg-[var(--solar-bg)]">
      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-center gap-3 mb-10">
          <div className="rounded-xl bg-[var(--solar-ink)] shadow-[var(--elev-2)] px-5 py-3 flex items-center justify-center">
            <img src={RAZ_LOGO} alt="RAZ Consulting" className="h-8 w-auto" onError={e => { (e.target as HTMLImageElement).style.display = "none"; }} />
          </div>
          <p className="text-[var(--solar-mute)] text-[11px] tracking-[0.2em] uppercase font-medium">Intelligence Hub</p>
        </div>
        <div className="rounded-xl border border-[var(--solar-line)] bg-white shadow-[var(--elev-2)] overflow-hidden">
          <div className="p-8">
            {!success ? (
              <>
                <div className="mb-6">
                  <div className="w-12 h-12 rounded-full bg-[var(--brand-soft)] flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-[var(--brand)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                  </div>
                  <h1 className="text-[var(--solar-ink)] text-xl font-semibold mb-1">Criar sua senha</h1>
                  <p className="text-[var(--ink-2)] text-sm">Você foi convidado para o Intelligence Hub. Defina uma senha para ativar seu acesso.</p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-[11px] mb-1.5 uppercase tracking-wide font-semibold text-[var(--solar-mute)]">Nova senha</label>
                    <div className="relative">
                      <input type={showPassword ? "text" : "password"} required value={password} onChange={e => setPassword(e.target.value)} placeholder="Mínimo 8 caracteres" className="w-full bg-white border border-[var(--solar-line)] text-[var(--solar-ink)] rounded-[10px] px-4 py-3 text-sm placeholder-[var(--solar-mute)] focus:outline-none focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20 transition pr-10" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--solar-mute)] hover:text-[var(--solar-ink)] transition">{showPassword ? <EyeOffIcon /> : <EyeIcon />}</button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] mb-1.5 uppercase tracking-wide font-semibold text-[var(--solar-mute)]">Confirmar senha</label>
                    <div className="relative">
                      <input type={showConfirm ? "text" : "password"} required value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Repita a senha" className="w-full bg-white border border-[var(--solar-line)] text-[var(--solar-ink)] rounded-[10px] px-4 py-3 text-sm placeholder-[var(--solar-mute)] focus:outline-none focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20 transition pr-10" />
                      <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--solar-mute)] hover:text-[var(--solar-ink)] transition">{showConfirm ? <EyeOffIcon /> : <EyeIcon />}</button>
                    </div>
                  </div>
                  {password.length > 0 && (
                    <div className="flex gap-1">
                      {[...Array(4)].map((_, i) => (
                        <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${password.length >= 12 ? "bg-[var(--brand)]" : password.length >= 10 ? (i < 3 ? "bg-[var(--brand)]" : "bg-[var(--solar-line)]") : password.length >= 8 ? (i < 2 ? "bg-[var(--brand)]" : "bg-[var(--solar-line)]") : (i < 1 ? "bg-[var(--brand)]" : "bg-[var(--solar-line)]")}`} />
                      ))}
                    </div>
                  )}
                  {error && <p className="text-[var(--destructive)] text-sm">{error}</p>}
                  <button type="submit" disabled={acceptMutation.isPending} className="w-full bg-[var(--solar-ink)] text-white font-semibold py-3 rounded-[10px] text-sm shadow-[var(--elev-1)] hover:opacity-90 transition disabled:opacity-50 mt-2">
                    {acceptMutation.isPending ? "Ativando acesso..." : "Ativar acesso →"}
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center py-4">
                <div className="w-14 h-14 rounded-full bg-[var(--brand-soft)] flex items-center justify-center mx-auto mb-6">
                  <svg className="w-7 h-7 text-[var(--brand)]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                </div>
                <h2 className="text-[var(--solar-ink)] text-lg font-semibold mb-2">Acesso ativado!</h2>
                <p className="text-[var(--ink-2)] text-sm">Redirecionando para o painel...</p>
              </div>
            )}
          </div>
        </div>
        <p className="text-center text-[var(--solar-mute)] text-xs mt-6">Problemas? <a href="mailto:marketing@razconsulting.com.br" className="text-[var(--solar-ink)] hover:underline transition">marketing@razconsulting.com.br</a></p>
      </div>
    </div>
  );
}
